import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n";
import GroupTile from "./GroupTile";
import ProductTile from "./ProductTile";

const EmptyState = ({ search }) => {
  const { t } = useI18n();

  return (
    <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
      <span className="text-5xl mb-3">🔍</span>
      <p className="text-slate-300 font-semibold">{t("pos.grid.noMatch")}</p>
      <p className="text-slate-500 text-sm mt-1">
        {search
          ? t("pos.grid.nothingFor", { term: search })
          : t("pos.grid.emptyCategory")}
      </p>
    </div>
  );
};

/**
 * A shop with nothing on its shelf yet — not "no match for this search". Says
 * what to do and goes there, because a till that only says "no product" to a
 * brand-new owner reads as broken.
 */
const NoProductsYet = () => {
  const { t } = useI18n();

  return (
    <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
      <span className="text-5xl mb-3">📦</span>
      <p className="text-slate-200 font-semibold">{t("pos.grid.shelfEmpty")}</p>
      <p className="text-slate-400 text-sm mt-1 max-w-xs">{t("pos.grid.shelfEmptyHint")}</p>
      <Link
        to="/admin/products"
        className="mt-5 rounded-lg bg-primary-600 px-5 py-3 font-semibold text-white"
      >
        {t("pos.grid.addProducts")}
      </Link>
    </div>
  );
};

/** Tiles are products, or ranges collapsed to one tile each (see utils/groups). */
const ProductGrid = ({
  tiles,
  quantityOf,
  lowStockThreshold,
  onAdd,
  onOpenGroup,
  search,
  shelfEmpty = false,
}) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2.5 sm:gap-3">
    {tiles.length === 0 ? (
      shelfEmpty ? <NoProductsYet /> : <EmptyState search={search} />
    ) : (
      tiles.map((tile) =>
        tile.type === "group" ? (
          <GroupTile
            key={tile.key}
            group={tile.group}
            members={tile.members}
            quantity={tile.members.reduce(
              (sum, member) => sum + quantityOf(member.id),
              0,
            )}
            lowStockThreshold={lowStockThreshold}
            onOpen={onOpenGroup}
          />
        ) : (
          <ProductTile
            key={tile.key}
            product={tile.product}
            quantity={quantityOf(tile.product.id)}
            lowStockThreshold={lowStockThreshold}
            onAdd={onAdd}
          />
        )
      )
    )}
  </div>
);

export default ProductGrid;
